import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  Bell,
  Users,
  Briefcase,
  CreditCard,
  ChevronRight,
  AlertCircle,
  CheckCircle,
  Clock,
  Coffee,
  ShoppingBag,
  Utensils,
  TrendingUp,
  DollarSign,
  Edit3,
  ExternalLink
} from 'lucide-react';
import Sidebar from './Sidebar';
import Topbar from './Topbar';
import { ApiProject, formatProjectNameWithType, getProjectList } from '../../utils/project';
import { formatDateFromApi } from '../../utils/dateTime';
import { getProjectProfileSetting } from '../../utils/projectConfig';

// Types
interface Project {
  id: string;
  name: string;
  projectType?: string;
  createdAt: string;
  services: ServiceType[];
  status: 'ACTIVE' | 'INACTIVE' | 'PENDING';
  monthlySpend: number; // TODO: billing API 연동 시 교체
}

type ServiceType = 'membership' | 'mobile-order' | 'showcase' | 'mall';

interface Announcement {
  id: string;
  title: string;
  date: string;
  type: 'info' | 'warning' | 'critical';
  isNew: boolean;
}

interface BillingInfo {
  currentMonth: number;
  lastMonth: number;
  lastMonthStatus: 'paid' | 'unpaid' | 'partial';
  unpaidAmount: number;
  dueDate: string;
}

interface UserInfo {
  accountId: string;
  userName: string;
  userId: string;
  role: string;
}

const Main: React.FC = () => {
  const navigate = useNavigate();
  const [isDesktopSidebarOpen, setIsDesktopSidebarOpen] = useState<boolean>(true);
  const [isMobileSidebarOpen, setIsMobileSidebarOpen] = useState<boolean>(false);
  const [isProfileDropdownOpen, setIsProfileDropdownOpen] = useState<boolean>(false);
  const [activeMenu, setActiveMenu] = useState<string>('home');

  const [projects, setProjects] = useState<Project[]>([]);
  const [isProjectsLoading, setIsProjectsLoading] = useState<boolean>(true);
  const [projectsError, setProjectsError] = useState<string>('');

  // TODO: 플랫폼 공지사항 API 연동 시 교체
  const announcements: Announcement[] = [
    // {
    //   id: 'ANN-001',
    //   title: '새로운 AI 분석 기능 출시',
    //   date: '2025-01-12',
    //   type: 'info',
    //   isNew: true
    // },
    // {
    //   id: 'ANN-002',
    //   title: '1월 15일 정기 점검 예정 (02:00 - 04:00)',
    //   date: '2025-01-10',
    //   type: 'warning',
    //   isNew: true
    // },
    // {
    //   id: 'ANN-003',
    //   title: '결제 시스템 업그레이드 완료',
    //   date: '2025-01-08',
    //   type: 'info',
    //   isNew: false
    // }
  ];

  const billingInfo: BillingInfo = {
    currentMonth: 0,
    lastMonth: 0,
    lastMonthStatus: 'paid',
    unpaidAmount: 0,
    dueDate: '2025-02-05'
  };

  // Service icon mapping
  const getServiceIcon = (service: ServiceType): React.ReactElement => {
    switch (service) {
      case 'membership':
        return <Users className="w-4 h-4" />;
      case 'mobile-order':
        return <Coffee className="w-4 h-4" />;
      case 'showcase':
        return <Utensils className="w-4 h-4" />;
      case 'mall':
        return <ShoppingBag className="w-4 h-4" />;
    }
  };

  const getServiceName = (service: ServiceType): string => {
    switch (service) {
      case 'membership':
        return '멤버십';
      case 'mobile-order':
        return '모바일오더';
      case 'showcase':
        return '쇼케이스';
      case 'mall':
        return '몰';
    }
  };

  const mapApiStatusToProjectStatus = (apiStatus: string): Project['status'] => {
    const normalizedStatus = apiStatus.toUpperCase();
    if (normalizedStatus === 'ACTIVE') return 'ACTIVE';
    if (normalizedStatus === 'INACTIVE') return 'INACTIVE';
    return 'PENDING';
  };

  const mapFeatureCodeToService = (featureCode: string): ServiceType | null => {
    switch (featureCode) {
      case 'MEMBERSHIP':
        return 'membership';
      case 'MOBILE_ORDER':
        return 'mobile-order';
      case 'SHOWCASE':
        return 'showcase';
      case 'MALL':
        return 'mall';
      default:
        return null;
    }
  };

  const fetchRecentProjects = async () => {
    setIsProjectsLoading(true);
    setProjectsError('');

    try {
      const response = await getProjectList({ keyword: '', status: '', type: '' });
      const list = response?.projects ?? [];

      const sortedByCreatedAtDesc = [...list].sort(
        (a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()
      );

      const featureCodeLists = await Promise.all(
        sortedByCreatedAtDesc.map((apiProject) =>
          getProjectProfileSetting(apiProject.code, 'PROJECT', 'FEATURE').catch(() => [] as string[])
        )
      );

      const transformedProjects = sortedByCreatedAtDesc.map((apiProject: ApiProject, index: number) => {
        const services = (featureCodeLists[index] ?? [])
          .map(mapFeatureCodeToService)
          .filter((service): service is ServiceType => service !== null);

        return {
          id: apiProject.code,
          name: apiProject.name,
          projectType: apiProject.projectType,
          createdAt: formatDateFromApi(apiProject.createdAt) || apiProject.createdAt,
          services: Array.from(new Set(services)),
          status: mapApiStatusToProjectStatus(apiProject.status),
          monthlySpend: 0,
        };
      });

      setProjects(transformedProjects);
    } catch (error: any) {
      console.error('최근 프로젝트 조회 오류:', error);
      setProjectsError(error?.message || '최근 프로젝트를 불러오는데 실패했습니다.');
      setProjects([]);
    } finally {
      setIsProjectsLoading(false);
    }
  };

  useEffect(() => {
    fetchRecentProjects();
  }, []);

  const formatCurrency = (amount: number): string => {
    return `₩${amount.toLocaleString()}`;
  };

  return (
    <div className="min-h-screen bg-gray-50 flex relative">
      <Sidebar
        isDesktopSidebarOpen={isDesktopSidebarOpen}
        setIsDesktopSidebarOpen={setIsDesktopSidebarOpen}
        isMobileSidebarOpen={isMobileSidebarOpen}
        setIsMobileSidebarOpen={setIsMobileSidebarOpen}
        isProfileDropdownOpen={isProfileDropdownOpen}
        setIsProfileDropdownOpen={setIsProfileDropdownOpen}
        activeMenu={activeMenu}
        setActiveMenu={setActiveMenu}
      />

      {/* Main Content */}
      <div className="flex-1 flex flex-col min-w-0">
        <Topbar
          title="홈"
          isMobileSidebarOpen={isMobileSidebarOpen}
          setIsMobileSidebarOpen={setIsMobileSidebarOpen}
          isProfileDropdownOpen={isProfileDropdownOpen}
          setIsProfileDropdownOpen={setIsProfileDropdownOpen}
        />

        {/* Dashboard Content */}
        <main className="flex-1 p-4 sm:p-6 overflow-auto">
          <div className="max-w-7xl mx-auto space-y-4 sm:space-y-6">
            {/* Platform Announcements */}
            <div className="bg-white rounded-lg shadow-sm border border-gray-200">
              <div className="p-4 sm:p-6">
                <div className="flex items-center justify-between mb-4">
                  <h2 className="text-base sm:text-lg font-semibold text-gray-900 flex items-center">
                    <Bell className="w-4 sm:w-5 h-4 sm:h-5 mr-2" />
                    <span className="hidden sm:inline">플랫폼 공지사항</span>
                    <span className="sm:hidden">공지사항</span>
                  </h2>
                  <button className="text-xs sm:text-sm text-blue-600 hover:underline flex items-center">
                    전체 보기
                    <ChevronRight className="w-3 sm:w-4 h-3 sm:h-4 ml-1" />
                  </button>
                </div>
                <div className="space-y-2 sm:space-y-3">
                  {announcements.length === 0 ? (
                    <div className="flex flex-col items-center justify-center py-8 text-center">
                      <Bell className="w-8 h-8 text-gray-300 mb-2" />
                      <p className="text-sm text-gray-500">공지사항이 없습니다.</p>
                    </div>
                  ) : (
                    announcements.map((announcement) => (
                      <div
                        key={announcement.id}
                        className="flex items-start p-2 sm:p-3 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors cursor-pointer"
                      >
                        <div className="mr-2 sm:mr-3 mt-0.5">
                          {announcement.type === 'warning' ? (
                            <AlertCircle className="w-4 sm:w-5 h-4 sm:h-5 text-yellow-500" />
                          ) : announcement.type === 'critical' ? (
                            <AlertCircle className="w-4 sm:w-5 h-4 sm:h-5 text-red-500" />
                          ) : (
                            <CheckCircle className="w-4 sm:w-5 h-4 sm:h-5 text-blue-500" />
                          )}
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center">
                            <p className="text-xs sm:text-sm font-medium text-gray-900 truncate">
                              {announcement.title}
                            </p>
                            {announcement.isNew && (
                              <span className="ml-2 px-1.5 sm:px-2 py-0.5 bg-red-500 text-white text-xs rounded-full flex-shrink-0">
                                NEW
                              </span>
                            )}
                          </div>
                          <p className="text-xs text-gray-500 mt-1">{announcement.date}</p>
                        </div>
                      </div>
                    ))
                  )}
                </div>
              </div>
            </div>

            {/* Projects and Billing Row */}
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 sm:gap-6">
              {/* Recent Projects - 2 columns on desktop */}
              <div className="lg:col-span-2 bg-white rounded-lg shadow-sm border border-gray-200">
                <div className="p-4 sm:p-6">
                  <div className="flex items-center justify-between mb-4">
                    <h2 className="text-base sm:text-lg font-semibold text-gray-900 flex items-center">
                      <Briefcase className="w-4 sm:w-5 h-4 sm:h-5 mr-2" />
                      최근 프로젝트
                    </h2>
                    <button className="text-xs sm:text-sm text-blue-600 hover:underline flex items-center">
                      전체 프로젝트
                      <ChevronRight className="w-3 sm:w-4 h-3 sm:h-4 ml-1" />
                    </button>
                  </div>
                  <div className="space-y-2 sm:space-y-3">
                    {isProjectsLoading && (
                      <div className="p-4 bg-gray-50 rounded-lg text-sm text-gray-500">
                        최근 프로젝트를 불러오는 중...
                      </div>
                    )}
                    {!isProjectsLoading && projectsError && (
                      <div className="p-4 bg-red-50 rounded-lg text-sm text-red-600">
                        {projectsError}
                      </div>
                    )}
                    {!isProjectsLoading && !projectsError && projects.slice(0, 5).map((project) => (
                      <div
                        key={project.id}
                        className="flex flex-col sm:flex-row sm:items-center sm:justify-between p-3 sm:p-4 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors cursor-default"
                      >
                        <div className="flex items-center space-x-3 sm:space-x-4 mb-2 sm:mb-0">
                          <div className={`w-2 h-2 rounded-full flex-shrink-0 ${
                            project.status === 'ACTIVE' ? 'bg-green-500' : 
                            project.status === 'INACTIVE' ? 'bg-gray-400' : 'bg-yellow-500'
                          }`} />
                          <div className="min-w-0">
                            <p className="font-medium text-gray-900 text-sm sm:text-base truncate">
                              {formatProjectNameWithType(project.name, project.projectType)}
                            </p>
                            <p className="text-xs sm:text-sm text-gray-500 truncate">
                              {project.id} • {project.createdAt}
                            </p>
                          </div>
                        </div>
                        
                        <div className="flex items-center justify-between sm:space-x-6 ml-5 sm:ml-0">
                          <div className="flex items-center space-x-1 sm:space-x-2">
                            {project.services.slice(0, 4).map((service, idx) => {
                              const icon = getServiceIcon(service);
                              return (
                                <div
                                  key={idx}
                                  className="p-1.5 sm:p-2 bg-white rounded-lg border border-gray-200"
                                  title={getServiceName(service)}
                                >
                                  <div className="w-3 sm:w-4 h-3 sm:h-4">
                                    {icon}
                                  </div>
                                </div>
                              );
                            })}
                            {project.services.length === 0 && (
                              <span className="text-xs text-gray-500">서비스 설정 없음</span>
                            )}
                          </div>
                          
                          <div className="flex items-center space-x-1 sm:space-x-2">
                            <button
                              className="p-1.5 sm:p-2 hover:bg-gray-200 rounded-lg transition-colors"
                              title="프로젝트 편집"
                              onClick={(e) => {
                                e.stopPropagation();
                                navigate(`/projects/${project.id}/update`);
                              }}
                            >
                              <Edit3 className="w-3 sm:w-4 h-3 sm:h-4 text-gray-600" />
                            </button>
                            <button
                              className="p-1.5 sm:p-2 hover:bg-gray-200 rounded-lg transition-colors"
                              title="프로젝트 대시보드"
                              onClick={(e) => {
                                e.stopPropagation();
                                navigate(`/projects/${project.id}/dashboard`);
                              }}
                            >
                              <ExternalLink className="w-3 sm:w-4 h-3 sm:h-4 text-gray-600" />
                            </button>
                          </div>
                        </div>
                      </div>
                    ))}
                    {!isProjectsLoading && !projectsError && projects.length === 0 && (
                      <div className="p-4 bg-gray-50 rounded-lg text-sm text-gray-500">
                        표시할 프로젝트가 없습니다.
                      </div>
                    )}
                  </div>
                </div>
              </div>

              {/* Billing Information */}
              <div className="bg-white rounded-lg shadow-sm border border-gray-200">
                <div className="p-4 sm:p-6">
                  <h2 className="text-base sm:text-lg font-semibold text-gray-900 flex items-center mb-4">
                    <CreditCard className="w-4 sm:w-5 h-4 sm:h-5 mr-2" />
                    이번달 요금
                  </h2>
                  
                  <div className="space-y-3 sm:space-y-4">
                    {/* Current Month */}
                    <div className="p-3 sm:p-4 bg-blue-50 rounded-lg">
                      <p className="text-xs sm:text-sm text-gray-600 mb-1">1월 예상 요금</p>
                      <p className="text-xl sm:text-2xl font-bold text-gray-900">
                        {formatCurrency(billingInfo.currentMonth)}
                      </p>
                      <p className="text-xs text-gray-500 mt-2">
                        납부 예정일: {billingInfo.dueDate}
                      </p>
                    </div>

                    {/* Last Month */}
                    <div className="space-y-2 sm:space-y-3">
                      <div className="flex items-center justify-between">
                        <span className="text-xs sm:text-sm text-gray-600">지난달 요금</span>
                        <span className="text-xs sm:text-sm font-medium text-gray-900">
                          {formatCurrency(billingInfo.lastMonth)}
                        </span>
                      </div>
                      
                      <div className="flex items-center justify-between">
                        <span className="text-xs sm:text-sm text-gray-600">납부 상태</span>
                        <div className="flex items-center">
                          {billingInfo.lastMonthStatus === 'paid' ? (
                            <>
                              <CheckCircle className="w-3 sm:w-4 h-3 sm:h-4 text-green-500 mr-1" />
                              <span className="text-xs sm:text-sm text-green-600">완납</span>
                            </>
                          ) : billingInfo.lastMonthStatus === 'unpaid' ? (
                            <>
                              <AlertCircle className="w-3 sm:w-4 h-3 sm:h-4 text-red-500 mr-1" />
                              <span className="text-xs sm:text-sm text-red-600">미납</span>
                            </>
                          ) : (
                            <>
                              <Clock className="w-3 sm:w-4 h-3 sm:h-4 text-yellow-500 mr-1" />
                              <span className="text-xs sm:text-sm text-yellow-600">부분납부</span>
                            </>
                          )}
                        </div>
                      </div>

                      {billingInfo.unpaidAmount > 0 && (
                        <div className="p-2 sm:p-3 bg-red-50 rounded-lg">
                          <p className="text-xs sm:text-sm text-red-800">
                            미납금액: {formatCurrency(billingInfo.unpaidAmount)}
                          </p>
                        </div>
                      )}
                    </div>

                    {/* <button className="w-full py-2 bg-gray-900 text-white rounded-lg hover:bg-gray-800 transition-colors text-xs sm:text-sm">
                      결제 관리
                    </button> */}
                  </div>
                </div>
              </div>
            </div>

            {/* Quick Stats */}
            {/* <div className="grid grid-cols-2 md:grid-cols-4 gap-3 sm:gap-4">
              <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-3 sm:p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-xs sm:text-sm text-gray-600">총 프로젝트</p>
                    <p className="text-xl sm:text-2xl font-bold text-gray-900 mt-1">
                      {projects.length}
                    </p>
                  </div>
                  <Briefcase className="w-6 sm:w-8 h-6 sm:h-8 text-gray-300" />
                </div>
              </div>

              <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-3 sm:p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-xs sm:text-sm text-gray-600">활성 프로젝트</p>
                    <p className="text-xl sm:text-2xl font-bold text-gray-900 mt-1">
                      {projects.filter(p => p.status === 'ACTIVE').length}
                    </p>
                  </div>
                  <CheckCircle className="w-6 sm:w-8 h-6 sm:h-8 text-green-300" />
                </div>
              </div>

              <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-3 sm:p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-xs sm:text-sm text-gray-600">월 총 비용</p>
                    <p className="text-xl sm:text-2xl font-bold text-gray-900 mt-1">
                      ₩{Math.floor(projects.reduce((sum, p) => sum + p.monthlySpend, 0) / 1000)}K
                    </p>
                  </div>
                  <DollarSign className="w-6 sm:w-8 h-6 sm:h-8 text-blue-300" />
                </div>
              </div>

              <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-3 sm:p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-xs sm:text-sm text-gray-600">새 알림</p>
                    <p className="text-xl sm:text-2xl font-bold text-gray-900 mt-1">
                      {announcements.filter(a => a.isNew).length}
                    </p>
                  </div>
                  <Bell className="w-6 sm:w-8 h-6 sm:h-8 text-red-300" />
                </div>
              </div>
            </div> */}
          </div>
        </main>
      </div>
    </div>
  );
};

export default Main;